import { Badge, Button } from '@palmetto/palmetto-components';
import type { Column, Row } from '@palmetto/palmetto-components';
import { useCallback } from 'react';
import { Link } from 'react-router-dom';

import { PricingPlanStatus } from 'types';

import type { SeasonalPricingPlan } from '../../../services/pricing';

import { BLUE_BAR, BLUE_CELL, MONTH_KEYS, ORANGE_BAR, ORANGE_CELL, seasonalPlansPath } from './constants';
import type { MonthKey } from './constants';

const SHORT_MONTH: Record<MonthKey, string> = {
  jan: 'Jan',
  feb: 'Feb',
  mar: 'Mar',
  apr: 'Apr',
  may: 'May',
  jun: 'Jun',
  jul: 'Jul',
  aug: 'Aug',
  sep: 'Sep',
  oct: 'Oct',
  nov: 'Nov',
  dec: 'Dec',
};

/** Every column applies this so an archived row reads as dimmed across all of its cells. */
const rowOpacityClass = (row?: Row): string => (row?.status === PricingPlanStatus.archived ? 'opacity-50' : '');

function MonthCell({ value }: { value: number }) {
  if (value === 100) {
    return <span className="text-sm text-[var(--color-text-contrast)]">-</span>;
  }
  const bg = value > 100 ? ORANGE_CELL : BLUE_CELL;
  return (
    <span
      className="rounded-sm inline-flex items-center justify-center px-[6px] py-[2px] text-sm text-[#44474b]"
      style={{ backgroundColor: bg }}
    >
      {value}%
    </span>
  );
}

function TwelveMonthPattern({ multipliers }: { multipliers?: SeasonalPricingPlan['monthlyMultipliers'] }) {
  return (
    <div className="relative" style={{ width: 160, height: 24 }}>
      <svg
        className="pointer-events-none absolute inset-0"
        width="100%"
        height="100%"
        overflow="visible"
        style={{ top: '50%', transform: 'translateY(-50%)', height: 1 }}
      >
        <line x1="0" y1="0" x2="100%" y2="0" stroke="#6e727a" strokeWidth="1" strokeDasharray="3 2" />
      </svg>

      <div className="grid h-full" style={{ gridTemplateColumns: 'repeat(12, 1fr)', gap: '0 2px' }}>
        {MONTH_KEYS.map((key) => {
          const v = multipliers?.[key] ?? 100;
          const aboveColor = v > 100 ? ORANGE_BAR : 'transparent';
          const belowColor = v < 100 ? BLUE_BAR : 'transparent';
          return (
            <div key={key} className="flex flex-col">
              <div
                style={{
                  flex: 1,
                  backgroundColor: aboveColor,
                  borderRadius: '1px 1px 0 0',
                }}
              />
              <div
                style={{
                  flex: 1,
                  backgroundColor: belowColor,
                  borderRadius: '0 0 1px 1px',
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface ArchiveButtonCellProps {
  planGroupId?: string;
  isArchived: boolean;
  onArchive: (planGroupId: string) => void;
}

/** Extracted so the row-specific onClick can live in its own useCallback instead of an inline arrow in the column's render function. */
function ArchiveButtonCell({ planGroupId, isArchived, onArchive }: ArchiveButtonCellProps) {
  const handleClick = useCallback(() => {
    if (planGroupId) onArchive(planGroupId);
  }, [onArchive, planGroupId]);

  return (
    <Button as="button" variant="secondary" tone="neutral" size="sm" onClick={handleClick} isDisabled={isArchived}>
      Archive
    </Button>
  );
}

export function buildSeasonalPlanColumns(onArchive: (planGroupId: string) => void): Column[] {
  return [
    {
      key: 'name',
      heading: 'Name',
      width: 180,
      cellClassName: (_cell, row) => rowOpacityClass(row),
      render: (_cell, row) => {
        const isArchived = row?.status === PricingPlanStatus.archived;
        return (
          <span className="inline-flex items-center gap-2">
            <Link
              to={`${seasonalPlansPath(row?.programType)}/${row?.planGroupId}`}
              className={`text-sm font-medium hover:underline ${
                isArchived ? 'text-[var(--color-text-body-secondary)]' : 'text-[var(--color-text-contrast)]'
              }`}
            >
              {row?.name}
            </Link>
            {row?.annualEquivalent === false && <Badge variant="warning" message="Not equivalent" />}
          </span>
        );
      },
    },
    ...MONTH_KEYS.map((key): Column => ({
      key,
      heading: SHORT_MONTH[key],
      width: 20,
      align: 'center',
      headerClassName: '!px-0',
      cellClassName: (_cell, row) => `!px-0 ${rowOpacityClass(row)}`.trim(),
      render: (_cell, row) => <MonthCell value={row?.monthlyMultipliers?.[key] ?? 100} />,
    })),
    {
      key: 'pattern',
      heading: '12-Month Pattern',
      width: 100,
      headerClassName: '!px-[1px]',
      cellClassName: (_cell, row) => `!px-[1px] ${rowOpacityClass(row)}`.trim(),
      render: (_cell, row) => <TwelveMonthPattern multipliers={row?.monthlyMultipliers} />,
    },
    {
      key: 'status',
      heading: 'Status',
      width: 50,
      headerClassName: '!px-[1px]',
      cellClassName: (_cell, row) => `!px-[1px] ${rowOpacityClass(row)}`.trim(),
      render: (_cell, row) => {
        const isArchived = row?.status === PricingPlanStatus.archived;
        return <Badge variant={isArchived ? 'warning' : 'success'} message={isArchived ? 'Archived' : 'Active'} />;
      },
    },
    {
      key: 'manage',
      heading: 'Manage',
      width: 50,
      align: 'right',
      headerClassName: '!px-[1px]',
      cellClassName: (_cell, row) => `!px-[1px] ${rowOpacityClass(row)}`.trim(),
      render: (_cell, row) => (
        <ArchiveButtonCell
          planGroupId={row?.planGroupId}
          isArchived={row?.status === PricingPlanStatus.archived}
          onArchive={onArchive}
        />
      ),
    },
  ];
}
